import { useEffect, useRef } from "react";

// Trickle: one continuous deceleration toward the ceiling that never
// flattens out early — the curve keeps a real end velocity (its last control
// point sits below 1), so the line is still visibly moving when a fetch of
// up to ~3 s lands: ~20% at 200 ms, ~40% at 500 ms, ~55% at 1 s, ~78% at 2 s,
// parked at 90% at 3 s. Only a fetch slower than that meets a parked line,
// which is honest: it is late.
const TRICKLE_MS = 3000;
const TRICKLE_CEILING = "90%";
const TRICKLE_EASE = "cubic-bezier(0.1, 0.55, 0.55, 0.9)";
// Completion: pick up from wherever the trickle is and ACCELERATE into 100%
// (ease-in), so the motion reads as slowing → surging home, then fade out.
const FILL_MS = 240;
const FILL_EASE = "cubic-bezier(0.4, 0.05, 0.9, 0.3)";
const FADE_MS = 260;

/**
 * Thin top-edge progress line for the table's later fetches (next page, a
 * filter, a refresh). Unlike an indeterminate sweep it tracks the request:
 * `active` going true restarts it from 0 and decelerates toward 90%;
 * `active` going false accelerates it to 100% and fades it out. The track stays mounted
 * (transparent when idle) so restarts never flash. All motion is CSS
 * transitions driven through refs — nothing re-renders per frame.
 */
export default function FetchProgress({ active }: { active: boolean }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const track = trackRef.current;
    const bar = barRef.current;
    if (!track || !bar) return;
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];

    if (active) {
      // Restart from zero without animating backwards: kill transitions,
      // set the start state, flush layout, then let the trickle run.
      bar.style.transition = "none";
      track.style.transition = "none";
      bar.style.width = "0%";
      track.style.opacity = "1";
      void bar.offsetWidth;
      bar.style.transition = `width ${TRICKLE_MS}ms ${TRICKLE_EASE}`;
      bar.style.width = TRICKLE_CEILING;
      return;
    }

    if (bar.style.width === "" || bar.style.width === "0%") return; // never started

    // A transition change mid-flight starts from the current width, so this
    // picks up exactly where the deceleration is.
    bar.style.transition = `width ${FILL_MS}ms ${FILL_EASE}`;
    bar.style.width = "100%";
    timers.current.push(
      window.setTimeout(() => {
        track.style.transition = `opacity ${FADE_MS}ms ease`;
        track.style.opacity = "0";
        timers.current.push(
          window.setTimeout(() => {
            bar.style.transition = "none";
            bar.style.width = "0%";
          }, FADE_MS)
        );
      }, FILL_MS)
    );
  }, [active]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  return (
    <div
      ref={trackRef}
      className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 bg-primary/15 opacity-0"
      role="progressbar"
      aria-label="Loading creators"
      aria-hidden={!active}
    >
      <div ref={barRef} className="h-full w-0 rounded-r-pill bg-primary" />
    </div>
  );
}
